import { FormattedMessage } from 'react-intl';
import { useCallback } from 'react';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import { Credentials, CredentialsEntity } from '../../models/credentials';
import { messages } from '../../components/screens/AwaitingCredentialsScreen/messages';
import { useCredentialsMachine, UseCredentialsMachineProps } from '../hooks/useCredentialsMachine';
import { BackButton } from '../../components/organisms/Header/HeaderMolecule';
import { ApplicationEvent } from '../../models/analytics';
import { useApplicationEvent } from '../../core/Analytics/useApplicationEvent';
import { SupplementalInformationMachineView } from '../screens/templates/SupplementalInformationMachineView';
import { AwaitingAccountsSelectionMachineView } from '../machines/awaitingAccountsSelectionMachine/AwaitingAccountsSelectionMachineView';
import { BankIdMachineView, BankIdMachineViewProps } from './BankIdMachineView';
import { TPRedirectMachineView } from './TPRedirectMachineView';

export type CredentialsMachineViewProps = UseCredentialsMachineProps &
  BackButton &
  Pick<BankIdMachineViewProps, 'bankIdTarget'>;

export const CredentialsMachineView = ({
  providedCredentials,
  onDone,
  onAuthError,
  provider,
  waitForUpdated = false,
  onBack,
  bankIdTarget,
}: CredentialsMachineViewProps) => {
  const [current, send] = useCredentialsMachine({
    providedCredentials,
    waitForUpdated,
    onDone,
    onAuthError,
    provider,
  });
  const { credentials, error } = current.context;
  const onMachineDone = useCallback(
    (credentialsChanged: CredentialsEntity) => {
      send({ type: 'MACHINE_DONE_EVENT', credentials: credentialsChanged });
    },
    [send]
  );

  const { sendEvent } = useApplicationEvent({
    applicationEvent: ApplicationEvent.CREDENTIALS_MACHINE,
  });

  if (current.matches('APPLICATION_ERROR_VIEW')) {
    sendEvent(ApplicationEvent.CREDENTIALS_MACHINE_ERROR);
    throw error;
  }

  if (current.matches('AWAITING_ACCOUNTS_SELECTION_MACHINE')) {
    return (
      <AwaitingAccountsSelectionMachineView
        credentials={credentials}
        onBack={onBack}
        onDone={onMachineDone}
      />
    );
  }

  if (current.matches('SUPPLEMENTAL_INFORMATION_MACHINE')) {
    return (
      <SupplementalInformationMachineView
        provider={provider}
        credentials={credentials}
        onDone={onMachineDone}
        onError={onMachineDone}
      />
    );
  }

  if (current.matches('BANKID_MACHINE')) {
    return (
      <BankIdMachineView
        provider={provider}
        credentials={credentials}
        bankIdTarget={bankIdTarget}
        onDone={onMachineDone}
        onBack={onBack}
      />
    );
  }

  if (current.matches('THIRD_PARTY_REDIRECT_MACHINE')) {
    return <TPRedirectMachineView provider={provider} credentials={credentials} onBack={onBack} />;
  }

  if (Credentials.isUpdating(current.context.credentials)) {
    return (
      <LoadingOverlay
        text={
          <FormattedMessage
            {...messages.connectingToAlternative}
            values={{ providerName: provider.displayName }}
          />
        }
      />
    );
  }

  return <LoadingOverlay />;
};
